import React, { Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router';
import SelfAssessment from './containers/SelfAssessment';
import PublicRoute from './hoc/PublicRoute';
import PrivateRoute from './hoc/PrivateRoute';
import SpecialRoutesRedirection from './hoc/SpecialRoutesRedirection';
import SurveyRoute from './hoc/SurveyRoute';

// components
import Loader from './ui-core/Loader';

// constants and css
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import MyFolders from './containers/Folders';
import Profile from './containers/Profile';
import Payment from './containers/Payment';
import NewHome from './containers/NewHome';
import PaymentProgressIndicator from './components/PaymentProgressIndicator';
import CartOffCanvas from './components/CartOffCanvas';

import { isLoggedIn } from './utils/helpers';
import { APP_ROUTES, COURSES_SLUGS } from './route';
import useUserRef from './hooks/useUserRef';
import { useDispatch, useSelector } from 'react-redux';
import { extractJSON } from './services/jsonUtil';
import { LOCAL_STORAGE_CONSTANT } from './constants/app';
import { doGetUserDataRequest } from './store/action/user';
import { ModalProvider } from './context/ModalContext';
import GlobalModalManager from './hoc/GlobalModalManager';
import YourRecommendedPlan from './containers/YourRecommendedPlan';
import { WebsiteSettingsProvider } from './contexts/WebsiteSettingsContext';
const OldProdcast = React.lazy(() =>
  import('./containers/Podcasts/components/OldProdcast/OldProdcast'),
);

// lazy loaded components
const CallbackController = React.lazy(() =>
  import('./containers/CallbackController'),
);
const JoinDeepLink = React.lazy(() =>
  import('./containers/JoinDeepLink/index.jsx'),
);
const Collection = React.lazy(() => import('./containers/Collection'));
const CollectionDetail = React.lazy(() =>
  import('./containers/CollectionDetail'),
);
const CuratedPlans = React.lazy(() => import('./containers/CuratedPlans'));
const DeatiledFolder = React.lazy(() => import('./containers/DetailedFolder'));
const DeatiledVideo = React.lazy(() => import('./containers/DetailedVideo'));
const FitnessOptions = React.lazy(() => import('./containers/FitnessOptions'));
const SignupOnboarding = React.lazy(() =>
  import('./containers/FitnessOptions/SignupOnboarding/SignupOnboarding'),
);
const AssessmentOnboarding = React.lazy(() =>
  import('./containers/AssessmentOptions/index'),
);
const GoalAssessment = React.lazy(() => import('./containers/GoalAssessment'));
// const MyFolders = React.lazy(() => import('./containers/Folders'));
const NewDashboard = React.lazy(() => import('./containers/NewDashboard'));
const HomeV2 = React.lazy(() => import('./containers/V2/Home'));
const CategoriesV2 = React.lazy(() => import('./containers/V2/Categories'));
const ThankYou = React.lazy(() => import('./containers/NewDashboard/ThankYou'));
// const NewHome = React.lazy(() => import('./containers/NewHome'));
const NoPageFound = React.lazy(() => import('./containers/NoPageFound'));
// const Payment = React.lazy(() => import('./containers/Payment'));

const PaymentAffiliation = React.lazy(() =>
  import('./containers/PaymentAffiliation'),
);
const PersonalDetails = React.lazy(() =>
  import('./containers/Profile/PersonalDetails'),
);
// const Profile = React.lazy(() => import('./containers/Profile'));
const QuickTips = React.lazy(() => import('./containers/QuickTips'));
const Search = React.lazy(() => import('./containers/Search'));
// const SelfAssessment = React.lazy(() => import('./containers/SelfAssessment'));
const Survey = React.lazy(() => import('./containers/Survey'));
const Challenge = React.lazy(() => import('./containers/Challenge'));
// const Challenges = React.lazy(() => import('./containers/Challenges'));
const CourseV2 = React.lazy(() =>
  import('./containers/Challenges/CourseV2/CourseV2'),
);
const BodyParts = React.lazy(() => import('./containers/BodyParts'));
const FullBodyLowImpact = React.lazy(() =>
  import('./containers/FullBodyLowImpact'),
);
const BareFootMiniCourse = React.lazy(() =>
  import('./containers/BareFootMiniCourse'),
);
const ChallengeDetail = React.lazy(() =>
  import('./containers/CuratedPlans/ChallengeDetail'),
);
const DetailedCategories = React.lazy(() =>
  import('./containers/DetailedCategories'),
);
const GratefulForBody = React.lazy(() =>
  import('./containers/GratefulForBody'),
);
const Gifts = React.lazy(() => import('./containers/Gifts'));
const Community = React.lazy(() => import('./containers/Community'));
const SuggestedGroups = React.lazy(() =>
  import('./containers/Community/SuggestedGroups'),
);
const MyGroups = React.lazy(() => import('./containers/Community/MyGroups'));
const GroupDetail = React.lazy(() =>
  import('./containers/Community/GroupDetail'),
);
const GroupMembers = React.lazy(() =>
  import('./containers/Community/GroupMembers'),
);
const MemberProfile = React.lazy(() =>
  import('./containers/Community/MemberProfile'),
);
const MemberProfileEdit = React.lazy(() =>
  import('./containers/Community/MemberProfileEdit'),
);
const CommunityNotification = React.lazy(() =>
  import('./containers/Community/CommunityNotification'),
);
const FullBodyMobility = React.lazy(() =>
  import('./containers/FullBodyMobility'),
);
const MoveToImprove = React.lazy(() => import('./containers/MoveToImprove'));
const Move2Improve = React.lazy(() => import('./containers/Move2Improve'));
const LiftingForLongevity = React.lazy(() =>
  import('./containers/LiftingForLongevity'),
);

const WarmupCoolDown = React.lazy(() => import('./containers/WarmupCoolDown'));
const PostureRedefined = React.lazy(() =>
  import('./containers/PostureRedefined'),
);

// Lead Pages
const Barefoot = React.lazy(() => import('./containers/Lead/Barefoot'));
const HipMobility = React.lazy(() => import('./containers/Lead/HipMobility'));
const KneeHealth = React.lazy(() => import('./containers/Lead/KneeHealth'));
const LowBackPain = React.lazy(() => import('./containers/Lead/LowBackPain'));
const ReleaseAndRecovery = React.lazy(() =>
  import('./containers/Lead/Release & Recover'),
);
const UpperBackAndNeck = React.lazy(() =>
  import('./containers/UpperBackAndNeck'),
);
const BundleDeals = React.lazy(() => import('./containers/BundleDeals'));
const OrderAffiliation = React.lazy(() =>
  import('./containers/OrderAffiliation'),
);
const PelvicFloor = React.lazy(() => import('./containers/PelvicFloor'));
const Blogs = React.lazy(() => import('./containers/Blogs'));
const OldBlog = React.lazy(() =>
  import('./containers/Blogs/components/OldBlog/OldBlog'),
);
const BlogsListing = React.lazy(() => import('./containers/Blogs/listingPage'));
const BlogDetail = React.lazy(() => import('./containers/Blogs/detailPage'));
const Podcasts = React.lazy(() => import('./containers/Podcasts'));
const Podcast = React.lazy(() => import('./containers/Podcasts/detailPage'));
const PodcastListing = React.lazy(() =>
  import('./containers/Podcasts/listingPage'),
);
const PrivacyPolicy = React.lazy(() => import('./containers/PrivacyPolicy'));
const TherapyPlans = React.lazy(() => import('./containers/V2/TherapyPlans'));
const PremiumCourses = React.lazy(() =>
  import('./containers/V2/PremiumCourses'),
);

const MobilityFoundations = React.lazy(() =>
  import('./containers/MobilityFoundations'),
);
const MobilityCoaching = React.lazy(() =>
  import('./containers/MobilityCoaching'),
);
const DiscoverPage = React.lazy(() => import('./containers/DiscoverPage'));

const LowBack = React.lazy(() => import('./containers/LowBack'));

// The v5 routes for these two paths used a render prop to switch on the
// slug. v6 elements take no props, so the switches live in components that
// read useParams() instead.
const CourseSlugRoute = () => {
  const { slug } = useParams();
  switch (slug) {
    case COURSES_SLUGS.BARE_FOOT:
      return <BareFootMiniCourse />;
    case COURSES_SLUGS.FULL_BODY:
      return <FullBodyLowImpact />;
    case COURSES_SLUGS.LowBack:
      return <LowBack />;
    case COURSES_SLUGS.GRATEFUL_FOR_BODY:
      return <GratefulForBody />;
    case COURSES_SLUGS.FULL_BODY_MOBILITY:
      return <FullBodyMobility />;
    case COURSES_SLUGS.UPPER_BACK_AND_NECK:
      return <UpperBackAndNeck />;
    case COURSES_SLUGS.MOVE_TO_IMPROVE:
      return <MoveToImprove />;
    case COURSES_SLUGS.PELVIC_FLOOR:
      return <PelvicFloor />;
    case COURSES_SLUGS.MOBILITY_FOUNDATIONS:
      return <MobilityFoundations />;
    case COURSES_SLUGS.MOBILITY_COACHING:
      return <MobilityCoaching />;
    case COURSES_SLUGS.MOVE_2_IMRPOVE:
      return <Move2Improve />;
    case COURSES_SLUGS.LIFTING_FOR_LONGEVITY:
      return <LiftingForLongevity />;
    case COURSES_SLUGS.WARMUP_COOL_DOWN:
      return <WarmupCoolDown />;
    case COURSES_SLUGS.POSTURE_REDEFINED:
      return <PostureRedefined />;
    case COURSES_SLUGS.HIP_MOBILITY:
      return <HipMobility />;
    default:
      return (
        <Navigate
          to={
            isLoggedIn()
              ? APP_ROUTES.PREMIUM_COURSES
              : APP_ROUTES.PAGE_NOT_FOUND
          }
          replace
        />
      );
  }
};

const RootRoute = () => {
  const session = extractJSON(LOCAL_STORAGE_CONSTANT.SESSION);
  if (session?.['access-token']) {
    return <Navigate to={APP_ROUTES.HOME} replace />;
  }
  return <PublicRoute component={NewHome} />;
};

const LeadSlugRoute = () => {
  const { slug } = useParams();
  switch (slug) {
    case COURSES_SLUGS.BARE_FOOT:
      return <Barefoot />;
    case COURSES_SLUGS.KNEE_HEALTH:
      return <KneeHealth />;
    case COURSES_SLUGS.LOW_BACK_PAIN:
      return <LowBackPain />;
    case COURSES_SLUGS.GRATEFUL_FOR_BODY:
      return <ReleaseAndRecovery />;
    default:
      return (
        <Navigate
          to={isLoggedIn() ? APP_ROUTES.HOME : APP_ROUTES.PAGE_NOT_FOUND}
          replace
        />
      );
  }
};

function App() {
  const { isUniversalLoading } = useSelector(state => state.common);
  function handleScroll(event) {
    const app = event.target;
    const header = document.getElementById('header-section');
    const mainBody = document.getElementById('main-body');
    const paddingTarget = mainBody || app.querySelector('.main');
    if (window.location.pathname !== APP_ROUTES.SELF_ASSESSMENT) {
      if (app.scrollTop >= 80) {
        if (header) header.classList.add('sticky-header');
        if (paddingTarget) paddingTarget.classList.add('main-body-pd');
      } else {
        if (header) header.classList.remove('sticky-header');
        if (paddingTarget) paddingTarget.classList.remove('main-body-pd');
      }
    }
  }

  const dispatch = useDispatch();
  const getUserData = () => {
    const user = extractJSON(LOCAL_STORAGE_CONSTANT.USER);
    if (user?.id) {
      const userId = user.id;
      dispatch(doGetUserDataRequest({ userId }));
    }
  };
  useEffect(() => {
    if (
      Object.values(COURSES_SLUGS).find(
        slugKey => window.location.pathname === `/${slugKey}`,
      )
    ) {
      getUserData();
    }
  }, []);

  useUserRef();
  return (
    <WebsiteSettingsProvider>
      <ModalProvider>
        <div id="app" className="App" onScroll={handleScroll}>
          <Suspense fallback={<Loader isLoading={true} />}>
            {/* <Router> */}
            <Routes>
              <Route path={APP_ROUTES.ROOT} element={<RootRoute />} />
              <Route
                path={APP_ROUTES.SUGGESTED_GROUPS}
                element={<PublicRoute component={SuggestedGroups} />}
              />
              <Route
                path={APP_ROUTES.MY_GROUPS}
                element={<PublicRoute component={MyGroups} />}
              />
              <Route
                path={APP_ROUTES.MEMBER_PROFILE_EDIT}
                element={<PublicRoute component={MemberProfileEdit} />}
              />
              <Route
                path={APP_ROUTES.MEMBER_PROFILE}
                element={<PublicRoute component={MemberProfile} />}
              />
              <Route
                path={APP_ROUTES.GROUP_MEMBERS}
                element={<PublicRoute component={GroupMembers} />}
              />
              <Route
                path={APP_ROUTES.GROUP_POST}
                element={<PublicRoute component={GroupDetail} />}
              />
              <Route
                path={APP_ROUTES.GROUP_DETAIL}
                element={<PublicRoute component={GroupDetail} />}
              />
              <Route
                path={APP_ROUTES.COMMUNITY_NOTIFICATIONS}
                element={<PublicRoute component={CommunityNotification} />}
              />
              <Route
                path={APP_ROUTES.COMMUNITY}
                element={<PublicRoute component={Community} />}
              />
              <Route
                path={APP_ROUTES.BLOGS}
                element={<PublicRoute component={Blogs} />}
              />
              <Route
                path={APP_ROUTES.YOUR_RECOMMENDED_PLAN}
                element={<YourRecommendedPlan />}
              />
              <Route
                path={APP_ROUTES.PODCASTS}
                element={<PublicRoute component={Podcasts} />}
              />
              <Route
                path={`${APP_ROUTES.BLOG}/*`}
                element={<PublicRoute component={BlogDetail} />}
              />
              <Route
                path={`${APP_ROUTES.BLOG_DUPLICATE}/*`}
                element={<PublicRoute component={OldBlog} />}
              />
              <Route
                path={APP_ROUTES.BLOGS_LISTING}
                element={<PublicRoute component={BlogsListing} />}
              />
              <Route
                path={APP_ROUTES.BLOGS_LISTINGS}
                element={<PublicRoute component={BlogsListing} />}
              />
              <Route
                path={APP_ROUTES.PODCAST}
                element={<PublicRoute component={Podcast} />}
              />
              <Route
                path={APP_ROUTES.PODCAST_DUPLICATE}
                element={<PublicRoute component={OldProdcast} />}
              />
              <Route
                path={APP_ROUTES.PODCASTS_LISTING}
                element={<PublicRoute component={PodcastListing} />}
              />
              <Route
                path={APP_ROUTES.PODCASTS_LISTINGS}
                element={<PublicRoute component={PodcastListing} />}
              />
              <Route
                path={APP_ROUTES.PRIVACY_POLICY}
                element={<PrivacyPolicy />}
              />
              <Route path={APP_ROUTES.GIFTS} element={<Gifts />} />
              <Route
                path={APP_ROUTES.FITNESS}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.FITNESS}
                    component={FitnessOptions}
                    hideFooter={true}
                  />
                }
              />
              <Route
                path={APP_ROUTES.SIGNUP_ONBOARDING}
                element={<SignupOnboarding />}
              />
              <Route
                path={APP_ROUTES.ASSESSMENT_ONBOARDING}
                element={<AssessmentOnboarding />}
              />
              <Route
                path={APP_ROUTES.GOAL_ASSESSMENT}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.GOAL_ASSESSMENT}
                    component={GoalAssessment}
                    hideFooter={true}
                  />
                }
              />
              <Route
                path={APP_ROUTES.HOME_OLD}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.HOME_OLD}
                    component={NewDashboard}
                  />
                }
              />
              <Route
                path={APP_ROUTES.HOME}
                element={
                  <PrivateRoute path={APP_ROUTES.HOME} component={HomeV2} />
                }
              />
              <Route
                path={APP_ROUTES.CATEGORIES}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.CATEGORIES}
                    component={CategoriesV2}
                  />
                }
              />
              <Route
                path={APP_ROUTES.THERAPY_PLANS}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.THERAPY_PLANS}
                    component={TherapyPlans}
                  />
                }
              />
              <Route
                path={APP_ROUTES.THERAPY_PLANS_DETAILS}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.THERAPY_PLANS_DETAILS}
                    component={CuratedPlans}
                  />
                }
              />
              <Route
                path={APP_ROUTES.DISCOVER_THERAPY_PLANS_DETAILED}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.DISCOVER_THERAPY_PLANS_DETAILED}
                    component={CuratedPlans}
                  />
                }
              />
              <Route
                path={APP_ROUTES.THERAPY_PLANS_DETAILED_VIDEO}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.THERAPY_PLANS_DETAILED_VIDEO}
                    component={DeatiledVideo}
                    enableAutoPlay={true}
                  />
                }
              />
              <Route
                path={APP_ROUTES.DISCOVER_THERAPY_PLANS_DETAILED_VIDEO}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.DISCOVER_THERAPY_PLANS_DETAILED_VIDEO}
                    component={DeatiledVideo}
                    enableAutoPlay={true}
                  />
                }
              />
              <Route
                path={APP_ROUTES.PREMIUM_COURSES}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.PREMIUM_COURSES}
                    component={PremiumCourses}
                  />
                }
              />
              <Route
                path={APP_ROUTES.THANK_YOU}
                element={<PublicRoute component={ThankYou} />}
              />
              <Route
                path={APP_ROUTES.PROFILE}
                element={
                  <PrivateRoute path={APP_ROUTES.PROFILE} component={Profile} />
                }
              />
              <Route
                path={APP_ROUTES.PROFILE_WITH_SUB_ROUTES}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.PROFILE_WITH_SUB_ROUTES}
                    component={Profile}
                  />
                }
              />
              <Route
                path={APP_ROUTES.COLLECTION}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.COLLECTION}
                    component={Collection}
                  />
                }
              />
              <Route
                path={APP_ROUTES.SEARCH}
                element={
                  <PrivateRoute path={APP_ROUTES.SEARCH} component={Search} />
                }
              />
              <Route
                path={APP_ROUTES.COLLECTION_DETAIL}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.COLLECTION_DETAIL}
                    component={CollectionDetail}
                    activeSection="Explore"
                  />
                }
              />
              <Route
                path={APP_ROUTES.MOST_POPULAR_DETAILED}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.MOST_POPULAR_DETAILED}
                    component={CollectionDetail}
                  />
                }
              />
              <Route
                path={APP_ROUTES.FOLDERS}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.FOLDERS}
                    component={MyFolders}
                  />
                }
              />
              <Route path={APP_ROUTES.COURSES} element={<CourseV2 />} />
              <Route
                path={APP_ROUTES.DISCOVER}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.DISCOVER}
                    component={DiscoverPage}
                  />
                }
              />
              <Route
                path={APP_ROUTES.DISCOVER_CATEGORIES_DETAILED}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.DISCOVER_CATEGORIES_DETAILED}
                    component={DetailedCategories}
                  />
                }
              />
              <Route
                path={APP_ROUTES.CATEGORIES_DETAILED}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.CATEGORIES_DETAILED}
                    component={DetailedCategories}
                  />
                }
              />
              <Route
                path={APP_ROUTES.DETAILED_FOLDER}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.DETAILED_FOLDER}
                    component={DeatiledFolder}
                  />
                }
              />
              <Route
                path={APP_ROUTES.QUICKTIPS}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.QUICKTIPS}
                    component={QuickTips}
                  />
                }
              />
              <Route
                path={APP_ROUTES.DISCOVER_QUICKTIPS_VIDEO}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.DISCOVER_QUICKTIPS_VIDEO}
                    component={QuickTips}
                  />
                }
              />
              <Route
                path={APP_ROUTES.MOST_POPULAR_DETAILED_QUICK_TIPS_VIDEO}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.MOST_POPULAR_DETAILED_QUICK_TIPS_VIDEO}
                    component={QuickTips}
                  />
                }
              />
              <Route
                path={APP_ROUTES.SELF_ASSESSMENT}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.SELF_ASSESSMENT}
                    component={SelfAssessment}
                  />
                }
              />
              <Route
                path={APP_ROUTES.CURATED_PLANS}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.CURATED_PLANS}
                    component={CuratedPlans}
                  />
                }
              />
              <Route
                path={APP_ROUTES.HOME_SUB_SECTION}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.HOME_SUB_SECTION}
                    component={CuratedPlans}
                  />
                }
              />
              <Route
                path={APP_ROUTES.COLLECTION_DETAILED_VIDEO}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.COLLECTION_DETAILED_VIDEO}
                    component={DeatiledVideo}
                  />
                }
              />
              <Route
                path={APP_ROUTES.MOST_POPULAR_DETAILED_VIDEO}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.MOST_POPULAR_DETAILED_VIDEO}
                    component={DeatiledVideo}
                  />
                }
              />
              <Route
                path={APP_ROUTES.CATEGORIES_DETAILED_VIDEO}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.CATEGORIES_DETAILED_VIDEO}
                    component={DeatiledVideo}
                  />
                }
              />
              <Route
                path={APP_ROUTES.DISCOVER_DETAILED_VIDEO}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.DISCOVER_DETAILED_VIDEO}
                    component={DeatiledVideo}
                  />
                }
              />
              <Route
                path={APP_ROUTES.SELF_ASSESSMENT_DETAILED_VIDEO}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.SELF_ASSESSMENT_DETAILED_VIDEO}
                    component={DeatiledVideo}
                    enableAutoPlay={true}
                  />
                }
              />
              <Route
                path={APP_ROUTES.PLANS_MADE_FOR_YOU_DETAILED_VIDEO}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.PLANS_MADE_FOR_YOU_DETAILED_VIDEO}
                    component={DeatiledVideo}
                    enableAutoPlay={true}
                  />
                }
              />
              <Route
                path={APP_ROUTES.HOME_SUB_SECTION_DETAILED_VIDEO}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.HOME_SUB_SECTION_DETAILED_VIDEO}
                    component={DeatiledVideo}
                    enableAutoPlay={true}
                  />
                }
              />
              <Route
                path={APP_ROUTES.FOLDERS_DETAILED_VIDEO}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.FOLDERS_DETAILED_VIDEO}
                    component={DeatiledVideo}
                    enableAutoPlay={true}
                  />
                }
              />
              <Route
                path={APP_ROUTES.PERSONAL_DETAILS}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.PERSONAL_DETAILS}
                    component={PersonalDetails}
                  />
                }
              />
              <Route path={APP_ROUTES.BUNDLE_DEALS} element={<BundleDeals />} />
              <Route path={APP_ROUTES.FREE_TRAIL} element={<Payment />} />
              <Route
                path={APP_ROUTES.SUCCESS}
                element={<PaymentAffiliation />}
              />
              <Route
                path={APP_ROUTES.CANCEL}
                element={<PaymentAffiliation />}
              />
              <Route
                path={APP_ROUTES.ORDER_CANCEL}
                element={<PaymentAffiliation />}
              />
              <Route
                path={APP_ROUTES.ORDER_CONFIRMATION}
                element={<OrderAffiliation />}
              />
              <Route
                path={APP_ROUTES.SURVEY}
                element={<SurveyRoute component={Survey} />}
              />
              <Route
                path={`${APP_ROUTES.LOGIN_CALLBACK}/*`}
                element={<CallbackController />}
              />
              <Route
                path={`${APP_ROUTES.CHALLENGE}/*`}
                element={<PublicRoute component={Challenge} />}
              />
              <Route
                path={`${APP_ROUTES.BODY_PARTS}/*`}
                element={<PublicRoute component={BodyParts} />}
              />
              {/* <Route path={APP_ROUTES.CHALLENGE_DETAIL} element={<PrivateRoute path={APP_ROUTES.CHALLENGE_DETAIL} component={ChallengeDetail} />} /> */}
              <Route
                path={APP_ROUTES.PREMIUM_COURSES_DETAILS}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.PREMIUM_COURSES_DETAILS}
                    component={ChallengeDetail}
                  />
                }
              />
              {/* <Route path={APP_ROUTES.CHALLENGE_TYPE_DETAILED_VIDEO} element={<PrivateRoute path={APP_ROUTES.CHALLENGE_TYPE_DETAILED_VIDEO} component={DeatiledVideo} enableAutoPlay={true} />} /> */}
              <Route
                path={APP_ROUTES.PREMIUM_COURSES_DETAILED_VIDEO}
                element={
                  <PrivateRoute
                    path={APP_ROUTES.PREMIUM_COURSES_DETAILED_VIDEO}
                    component={DeatiledVideo}
                    enableAutoPlay={true}
                  />
                }
              />
              <Route
                path={APP_ROUTES.BARE_FOOT_MINI_COURSE}
                element={
                  <SpecialRoutesRedirection
                    path={APP_ROUTES.BARE_FOOT_MINI_COURSE}
                  />
                }
              />
              <Route
                path={APP_ROUTES.GRATEFUL_FOR_BODY}
                element={
                  <SpecialRoutesRedirection
                    path={APP_ROUTES.GRATEFUL_FOR_BODY}
                  />
                }
              />
              <Route
                path={APP_ROUTES.FULL_BODY_MOBILITY}
                element={
                  <SpecialRoutesRedirection
                    path={APP_ROUTES.FULL_BODY_MOBILITY}
                  />
                }
              />
              <Route
                path={APP_ROUTES.OLD_GRATEFUL_FOR_BODY}
                element={
                  <SpecialRoutesRedirection
                    path={APP_ROUTES.OLD_GRATEFUL_FOR_BODY}
                  />
                }
              />
              <Route
                path={APP_ROUTES.PAGE_NOT_FOUND}
                element={<NoPageFound />}
              />
              <Route path={APP_ROUTES.JOIN} element={<JoinDeepLink />} />
              <Route
                path={APP_ROUTES.COURSE_SLUG}
                element={<CourseSlugRoute />}
              />
              <Route path={APP_ROUTES.LEAD_PAGES} element={<LeadSlugRoute />} />
              <Route
                path={APP_ROUTES.OLD_COURSES_BARE_FOOT}
                element={
                  <SpecialRoutesRedirection
                    path={APP_ROUTES.OLD_COURSES_BARE_FOOT}
                  />
                }
              />
              <Route
                path={APP_ROUTES.OLD_COURSES_FULL_BODY_LOW_IMPACT}
                element={
                  <SpecialRoutesRedirection
                    path={APP_ROUTES.OLD_COURSES_FULL_BODY_LOW_IMPACT}
                  />
                }
              />
              <Route
                path={APP_ROUTES.OLD_COURSES_GRATEFUL_FOR_BODY}
                element={
                  <SpecialRoutesRedirection
                    path={APP_ROUTES.OLD_COURSES_GRATEFUL_FOR_BODY}
                  />
                }
              />
              <Route
                path={APP_ROUTES.OLD_CHALLENGES_BARE_FOOT}
                element={
                  <SpecialRoutesRedirection
                    path={APP_ROUTES.OLD_CHALLENGES_BARE_FOOT}
                  />
                }
              />
              <Route
                path={APP_ROUTES.OLD_CHALLENGES_FULL_BODY_LOW_IMPACT}
                element={
                  <SpecialRoutesRedirection
                    path={APP_ROUTES.OLD_CHALLENGES_FULL_BODY_LOW_IMPACT}
                  />
                }
              />
              <Route
                path={APP_ROUTES.OLD_CHALLENGES_GRATEFUL_FOR_BODY}
                element={
                  <SpecialRoutesRedirection
                    path={APP_ROUTES.OLD_CHALLENGES_GRATEFUL_FOR_BODY}
                  />
                }
              />
              <Route
                path="*"
                element={
                  <Navigate
                    to={APP_ROUTES.PAGE_NOT_FOUND}
                    state={{ from: window.location.pathname }}
                    replace
                  />
                }
              />
            </Routes>
          </Suspense>
          <GlobalModalManager countryCode={'us'} />
          <PaymentProgressIndicator />
          <CartOffCanvas />
        </div>
        <Loader isLoading={isUniversalLoading} />
      </ModalProvider>
    </WebsiteSettingsProvider>
  );
}

export default App;
